/**
 * Healthcare Dashboard — Aether Health
 * Premium medical intelligence platform · lavender / orchid / mint-cyan.
 * Distinct icon-rail + asymmetric 12-col clinical grid, SVG vitals, ECG, donuts.
 */

export const HEALTHCARE_DASHBOARD_FILES = {
  'src/index.css': `@import url('https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700&family=Manrope:wght@400;500;600;700&family=JetBrains+Mono:wght@500;600&display=swap');

* { box-sizing: border-box; }
html { scroll-behavior: smooth; }
body {
  margin: 0;
  font-family: 'Manrope', system-ui, sans-serif;
  background: #F6F3FD;
  color: #1F1B3A;
  -webkit-font-smoothing: antialiased;
  font-feature-settings: 'ss01', 'cv11';
  overflow-x: hidden;
}
h1, h2, h3, h4, h5, h6, p { margin: 0; }
input { font-family: inherit; }
.font-display { font-family: 'Sora', system-ui, sans-serif; letter-spacing: -0.02em; }
.font-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-feature-settings: 'tnum'; }

/* Multi-line clamp + truncate helpers */
.ae-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; word-break: break-word; }
.ae-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; word-break: break-word; }
.ae-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; word-break: break-word; }
.ae-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.ae-tnum { font-feature-settings: 'tnum'; font-variant-numeric: tabular-nums; }

@keyframes ae-pulse {
  0% { box-shadow: 0 0 0 0 rgba(108, 92, 231, 0.45); }
  70% { box-shadow: 0 0 0 12px rgba(108, 92, 231, 0); }
  100% { box-shadow: 0 0 0 0 rgba(108, 92, 231, 0); }
}
@keyframes ae-drift {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
}
@keyframes ae-trace {
  from { stroke-dashoffset: 1200; }
  to { stroke-dashoffset: 0; }
}

.ae-rail-item { position: relative; transition: color 0.25s; }
.ae-rail-item::before {
  content: '';
  position: absolute; left: -22px; top: 50%;
  width: 3px; height: 0; border-radius: 0 4px 4px 0;
  background: linear-gradient(180deg, #6C5CE7, #8B7BF0);
  transition: height 0.3s, top 0.3s;
}
.ae-rail-item.active::before { height: 28px; top: calc(50% - 14px); }
.ae-rail-item.active { color: #6C5CE7; }

.ae-panel {
  background: #FFFFFF;
  border-radius: 20px;
  border: 1px solid #ECE6FB;
  box-shadow: 0 1px 0 rgba(31, 27, 58, 0.02), 0 24px 48px -32px rgba(108, 92, 231, 0.18);
}
.ae-panel-tinted {
  background: linear-gradient(180deg, #FBF9FF 0%, #F4EFFD 100%);
  border: 1px solid #E7E0F8;
  border-radius: 20px;
}
.ae-panel-ink {
  background: linear-gradient(160deg, #1B1736 0%, #2A2454 100%);
  border-radius: 20px;
  color: #E8E3FA;
  border: 1px solid rgba(155, 143, 232, 0.18);
}
.ae-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.ae-tag-mint { background: rgba(94, 197, 199, 0.14); color: #2E8B8D; }
.ae-tag-violet { background: rgba(108, 92, 231, 0.13); color: #5949C9; }
.ae-tag-amber { background: rgba(230, 167, 86, 0.16); color: #9B6A2D; }
.ae-tag-rose { background: rgba(232, 120, 142, 0.14); color: #B14463; }
.ae-tag-slate { background: rgba(97, 95, 126, 0.12); color: #4A4768; }

.ae-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 16px; border-radius: 12px;
  font-size: 12px; font-weight: 600;
  border: none; cursor: pointer;
  font-family: 'Manrope', sans-serif;
  transition: all 0.2s ease;
}
.ae-btn-primary {
  background: linear-gradient(135deg, #6C5CE7 0%, #8B7BF0 100%);
  color: #fff;
  box-shadow: 0 10px 22px -10px rgba(108, 92, 231, 0.55);
}
.ae-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 14px 28px -8px rgba(108, 92, 231, 0.7); }
.ae-btn-ghost {
  background: #FAF7FE;
  color: #4A4768;
  border: 1px solid #ECE6FB;
}
.ae-btn-ghost:hover { background: #F2EBFC; border-color: #DCD3F7; }

.ae-icon-tile {
  display: inline-flex; align-items: center; justify-content: center;
  width: 36px; height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #F2EBFC, #E7DDFA);
  color: #6C5CE7;
}

.ae-divider {
  height: 1px;
  background: linear-gradient(to right, transparent, #DCD3F7, transparent);
}

.ae-status-dot {
  width: 8px; height: 8px; border-radius: 999px;
  background: #5EC5C7;
  box-shadow: 0 0 0 4px rgba(94, 197, 199, 0.18);
  animation: ae-pulse 2.4s infinite;
}
.ae-status-dot-amber { background: #E6A756; box-shadow: 0 0 0 4px rgba(230, 167, 86, 0.18); }
.ae-status-dot-rose { background: #E8788E; box-shadow: 0 0 0 4px rgba(232, 120, 142, 0.18); }

.ae-grid-bg {
  background-image: radial-gradient(circle, rgba(108, 92, 231, 0.07) 1px, transparent 1.4px);
  background-size: 22px 22px;
}
`,

  'src/App.js': `import React from 'react';
import './index.css';
import SideRail from './components/SideRail';
import CommandBar from './components/CommandBar';
import ClinicalInsights from './components/ClinicalInsights';
import PatientFocus from './components/PatientFocus';
import CareQueue from './components/CareQueue';
import DepartmentMetrics from './components/DepartmentMetrics';
import AlertFeed from './components/AlertFeed';
import Footer from './components/Footer';

export default function App() {
  return (
    <div className="relative min-h-screen bg-[#F6F3FD] text-[#1F1B3A]">
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background:
            'radial-gradient(900px 600px at 88% -10%, rgba(108,92,231,0.16), transparent 60%), radial-gradient(700px 500px at -8% 30%, rgba(94,197,199,0.12), transparent 60%)',
        }}
      />
      <div className="relative z-10 flex">
        <SideRail />
        <div className="flex min-h-screen min-w-0 flex-1 flex-col">
          <CommandBar />
          <main className="min-w-0 flex-1 px-4 pb-10 pt-5 sm:px-6 md:px-8 lg:px-10">
            <section className="grid grid-cols-12 gap-4 md:gap-5">
              <div className="col-span-12 min-w-0 lg:col-span-3">
                <ClinicalInsights />
              </div>
              <div className="col-span-12 min-w-0 lg:col-span-6">
                <PatientFocus />
              </div>
              <div className="col-span-12 min-w-0 lg:col-span-3">
                <CareQueue />
              </div>
            </section>

            <section className="mt-4 grid grid-cols-12 gap-4 md:mt-5 md:gap-5">
              <div className="col-span-12 min-w-0 lg:col-span-8">
                <DepartmentMetrics />
              </div>
              <div className="col-span-12 min-w-0 lg:col-span-4">
                <AlertFeed />
              </div>
            </section>

            <Footer />
          </main>
        </div>
      </div>
    </div>
  );
}
`,

  'src/data/clinical.js': `export const RAIL_ITEMS = [
  { id: 'pulse', label: 'Overview', icon: 'pulse', active: true },
  { id: 'patients', label: 'Patients', icon: 'heart', count: 24 },
  { id: 'visits', label: 'Visits', icon: 'calendar', count: 8 },
  { id: 'records', label: 'Records', icon: 'file' },
  { id: 'imaging', label: 'Imaging', icon: 'scan' },
  { id: 'labs', label: 'Labs', icon: 'beaker' },
  { id: 'messages', label: 'Messages', icon: 'msg', count: 3 },
];

export const RAIL_FOOT = [
  { id: 'team', label: 'Team', icon: 'team' },
  { id: 'settings', label: 'Settings', icon: 'cog' },
];

export const COMMAND_METRICS = [
  { label: 'Live patients', value: '184', tone: 'mint' },
  { label: 'In OR', value: '7', tone: 'violet' },
  { label: 'Avg wait', value: '12m', tone: 'amber' },
  { label: 'Crit. alerts', value: '2', tone: 'rose' },
];

export const FOCUS_PATIENT = {
  name: 'Olivia Bennett',
  id: 'MRN-AE-204819',
  age: 34,
  sex: 'F',
  room: 'Suite 204 · West Wing',
  admitted: 'Today · 08:42',
  primary: 'Hypertensive crisis · stable',
  attending: 'Dr. Aria Hayes',
  riskScore: 32,
};

export const VITALS = [
  { label: 'Heart rate', unit: 'bpm', value: 78, min: 60, max: 110, color: '#E8788E', icon: 'heart' },
  { label: 'Blood pressure', unit: 'mmHg', value: '124/82', min: 0, max: 0, color: '#6C5CE7', icon: 'pressure', text: true },
  { label: 'SpO\\u2082', unit: '%', value: 98, min: 80, max: 100, color: '#5EC5C7', icon: 'spo2' },
  { label: 'Temperature', unit: '\\u00B0C', value: 36.7, min: 35, max: 39, color: '#E6A756', icon: 'temp' },
];

export const ECG_POINTS = [
  20, 22, 22, 20, 18, 22, 22, 12, 50, 0, 30, 22, 22, 22, 22, 22, 22, 22,
  22, 20, 22, 22, 20, 18, 22, 22, 12, 50, 0, 30, 22, 22, 22, 22, 22, 22,
  22, 20, 22, 22, 20, 18, 22, 22, 12, 50, 0, 30, 22, 22, 22, 22, 22, 22,
  22, 20, 22, 22, 20, 18, 22, 22, 12, 50, 0, 30, 22, 22, 22, 22, 22, 22,
];

export const INSIGHTS = [
  {
    title: 'Sepsis risk · low',
    desc: 'Composite score within normal range. Last reviewed 12m ago.',
    score: 18,
    tone: 'mint',
    chip: 'AI · monitored',
  },
  {
    title: 'Medication adherence',
    desc: 'Olivia missed 1 dose this week. Auto-reminder queued.',
    score: 76,
    tone: 'violet',
    chip: 'Smart nudge',
  },
  {
    title: 'Readmission likelihood',
    desc: 'Predicted 30-day · monitor BP trend overnight.',
    score: 42,
    tone: 'amber',
    chip: 'Predictive',
  },
];

export const COHORT_BARS = [
  { label: 'Cardio', value: 38 },
  { label: 'Endo', value: 22 },
  { label: 'Onco', value: 14 },
  { label: 'Neuro', value: 18 },
  { label: 'Gen', value: 31 },
];

export const CARE_QUEUE = [
  { time: '09:15', name: 'Sofia Patel', desc: 'Post-op vitals check', tone: 'mint', status: 'Now' },
  { time: '10:30', name: 'Marcus Chen', desc: 'Cardio consult · Dr. Rivera', tone: 'violet', status: 'Next' },
  { time: '11:00', name: 'Emma Lindqvist', desc: 'Imaging review', tone: 'violet', status: 'Scheduled' },
  { time: '13:45', name: 'James Okonkwo', desc: 'Endocrinology · HbA1c f/u', tone: 'amber', status: 'Watch' },
  { time: '16:15', name: 'Liam Goh', desc: 'Lab results · CBC + CMP', tone: 'slate', status: 'Scheduled' },
];

export const ALERTS = [
  { tone: 'rose', title: 'PT-4009 · HbA1c elevated', desc: '8.4% — exceeds 7.5 threshold.', time: '4m', kind: 'Critical' },
  { tone: 'amber', title: 'Imaging queue backlog', desc: '6 chest CT scans pending review.', time: '18m', kind: 'Alert' },
  { tone: 'violet', title: 'Dr. Rivera shared a note', desc: 'Post-op care plan for Marcus C.', time: '47m', kind: 'Inbox' },
  { tone: 'mint', title: 'Lab system synced', desc: 'All overnight results imported.', time: '1h', kind: 'System' },
];

export const DEPT_LINE = [
  { d: 'Mon', visits: 38, admits: 12 },
  { d: 'Tue', visits: 46, admits: 16 },
  { d: 'Wed', visits: 52, admits: 18 },
  { d: 'Thu', visits: 41, admits: 14 },
  { d: 'Fri', visits: 58, admits: 22 },
  { d: 'Sat', visits: 30, admits: 9 },
  { d: 'Sun', visits: 24, admits: 7 },
];

export const TREATMENT_MIX = [
  { name: 'Pharmacological', value: 46, color: '#6C5CE7' },
  { name: 'Surgical', value: 22, color: '#8B7BF0' },
  { name: 'Diagnostic', value: 18, color: '#5EC5C7' },
  { name: 'Therapeutic', value: 14, color: '#B7ACEF' },
];

export const DEPT_KPI = [
  { label: 'Bed occupancy', value: '78%', delta: '+3.2', good: true },
  { label: 'Avg LOS', value: '3.4d', delta: '-0.4', good: true },
  { label: 'Readmit rate', value: '5.1%', delta: '+0.6', good: false },
  { label: 'Outcome score', value: '9.2', delta: '+0.3', good: true },
];
`,

  'src/components/Glyph.js': `import React from 'react';

/** Tiny line-icon set used across the dashboard. */
export default function Glyph({ name, size = 18, className = '', strokeWidth = 1.7 }) {
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className,
  };
  switch (name) {
    case 'pulse':
      return (
        <svg {...props}><path d="M3 12h3l2-5 4 10 3-7 2 4h4" /></svg>
      );
    case 'heart':
      return (
        <svg {...props}><path d="M12 20s-7-4.5-9-9a4.5 4.5 0 0 1 8-3 4.5 4.5 0 0 1 8 3c-2 4.5-9 9-9 9z" /><path d="M6.5 11.5h2L10 14l2-5 1.5 3H17" /></svg>
      );
    case 'calendar':
      return (
        <svg {...props}><rect x="3.5" y="5" width="17" height="15" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4" /></svg>
      );
    case 'file':
      return (
        <svg {...props}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></svg>
      );
    case 'scan':
      return (
        <svg {...props}><path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2" /><circle cx="12" cy="12" r="3.4" /></svg>
      );
    case 'beaker':
      return (
        <svg {...props}><path d="M9 3h6M10 3v6.5L4.5 18.6A2 2 0 0 0 6.3 21.5h11.4a2 2 0 0 0 1.8-2.9L14 9.5V3" /><path d="M7.5 14h9" /></svg>
      );
    case 'msg':
      return (
        <svg {...props}><path d="M21 12a8 8 0 0 1-12.6 6.5L3 20l1.5-5.4A8 8 0 1 1 21 12z" /></svg>
      );
    case 'team':
      return (
        <svg {...props}><circle cx="9" cy="9" r="3.2" /><path d="M3 20a6 6 0 0 1 12 0M16 10.5a3 3 0 1 0 0-6M21 20a6 6 0 0 0-4-5.6" /></svg>
      );
    case 'cog':
      return (
        <svg {...props}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6h.01A1.65 1.65 0 0 0 10 3.09V3a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.32.7.92 1.18 1.69 1.31H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
      );
    case 'pressure':
      return (
        <svg {...props}><path d="M5 14a7 7 0 1 1 14 0" /><path d="M12 14l-3 4M12 4v2M19 14h2M3 14h2M16.5 8l1.5-1.5M6 8L4.5 6.5" /></svg>
      );
    case 'spo2':
      return (
        <svg {...props}><path d="M12 3s5 4.5 5 9a5 5 0 0 1-10 0c0-4.5 5-9 5-9z" /><path d="M9 13a3 3 0 0 0 6 0" /></svg>
      );
    case 'temp':
      return (
        <svg {...props}><path d="M14 4a2 2 0 1 0-4 0v9.5a4 4 0 1 0 4 0V4z" /><circle cx="12" cy="17" r="1.8" /></svg>
      );
    case 'search':
      return (
        <svg {...props}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
      );
    case 'plus':
      return (
        <svg {...props}><path d="M12 5v14M5 12h14" /></svg>
      );
    case 'bell':
      return (
        <svg {...props}><path d="M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8z" /><path d="M10 21a2 2 0 0 0 4 0" /></svg>
      );
    case 'sparkle':
      return (
        <svg {...props}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6" /></svg>
      );
    case 'arrow':
      return (
        <svg {...props}><path d="M5 12h14M13 6l6 6-6 6" /></svg>
      );
    case 'chev':
      return (
        <svg {...props}><path d="m9 6 6 6-6 6" /></svg>
      );
    case 'shield':
      return (
        <svg {...props}><path d="M12 3l8 3v6c0 4.5-3.5 8.5-8 9-4.5-.5-8-4.5-8-9V6l8-3z" /><path d="m9 12 2 2 4-4" /></svg>
      );
    default:
      return null;
  }
}
`,

  'src/components/SideRail.js': `import React from 'react';
import Glyph from './Glyph';
import { RAIL_ITEMS, RAIL_FOOT } from '../data/clinical';

function RailButton({ item }) {
  return (
    <button
      type="button"
      title={item.label}
      className={'ae-rail-item group flex h-11 w-11 items-center justify-center rounded-xl transition ' + (item.active ? 'active bg-[#F2EBFC]' : 'text-[#615F7E] hover:bg-[#F5F1FC] hover:text-[#6C5CE7]')}
    >
      <Glyph name={item.icon} size={19} />
      {item.count && (
        <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-md bg-[#6C5CE7] px-1 text-[9px] font-bold text-white">
          {item.count}
        </span>
      )}
    </button>
  );
}

export default function SideRail() {
  return (
    <aside className="sticky top-0 hidden h-screen w-[78px] shrink-0 flex-col items-center border-r border-[#ECE6FB] bg-white/65 py-5 backdrop-blur-xl lg:flex">
      <div className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#6C5CE7] to-[#8B7BF0] text-white shadow-[0_10px_24px_-12px_rgba(108,92,231,0.6)]">
        <Glyph name="pulse" size={20} strokeWidth={2.1} />
      </div>
      <p className="font-display mt-2 text-[10px] font-bold tracking-[0.18em] text-[#6C5CE7]">AE</p>

      <div className="mt-8 flex flex-col items-center gap-3 relative">
        {RAIL_ITEMS.map((item) => (
          <div key={item.id} className="relative">
            <RailButton item={item} />
          </div>
        ))}
      </div>

      <div className="mt-auto flex flex-col items-center gap-3">
        {RAIL_FOOT.map((item) => (
          <RailButton key={item.id} item={item} />
        ))}
        <div className="mt-2 flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#6C5CE7] to-[#5EC5C7] text-[10px] font-bold text-white">
          AH
        </div>
      </div>
    </aside>
  );
}
`,

  'src/components/CommandBar.js': `import React from 'react';
import Glyph from './Glyph';
import { COMMAND_METRICS } from '../data/clinical';

const TONE_DOT = { mint: 'bg-[#5EC5C7]', violet: 'bg-[#6C5CE7]', amber: 'bg-[#E6A756]', rose: 'bg-[#E8788E]' };

export default function CommandBar() {
  return (
    <header className="sticky top-0 z-30 border-b border-[#ECE6FB] bg-white/70 backdrop-blur-2xl">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6 sm:gap-4 md:flex-nowrap md:px-8 md:py-3.5 lg:px-10">
        <div className="min-w-0 max-w-[180px] shrink-0 sm:max-w-[220px]">
          <p className="font-display ae-truncate text-[14px] font-bold leading-tight text-[#1F1B3A] sm:text-[15px]">Aether Health</p>
          <p className="ae-truncate hidden text-[11px] font-medium text-[#615F7E] sm:block">Saint Lumea Medical Center · Floor 2</p>
        </div>

        <div className="order-3 flex h-9 w-full min-w-0 items-center gap-2 rounded-xl border border-[#ECE6FB] bg-[#FAF7FE] px-3 sm:gap-3 sm:px-4 md:order-none md:max-w-[420px] md:flex-1">
          <Glyph name="search" size={15} className="shrink-0 text-[#8B85AB]" />
          <input
            readOnly
            placeholder="Search patients, MRN, clinicians"
            className="ae-truncate min-w-0 flex-1 border-0 bg-transparent text-[12.5px] text-[#1F1B3A] outline-none placeholder:text-[#9C97B5]"
          />
          <span className="hidden shrink-0 rounded-md border border-[#ECE6FB] bg-white px-1.5 py-0.5 text-[10px] font-semibold text-[#8B85AB] sm:inline">⌘ K</span>
        </div>

        <div className="hidden shrink-0 items-center gap-2 xl:flex">
          {COMMAND_METRICS.map((m) => (
            <div key={m.label} className="flex shrink-0 items-center gap-2 rounded-xl border border-[#ECE6FB] bg-white px-3 py-1.5">
              <span className={'h-2 w-2 shrink-0 rounded-full ' + TONE_DOT[m.tone]} />
              <div className="leading-none">
                <p className="font-mono ae-tnum text-[12px] font-semibold text-[#1F1B3A]">{m.value}</p>
                <p className="mt-1 whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.12em] text-[#8B85AB]">{m.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-2">
          <button type="button" className="ae-btn ae-btn-ghost hidden sm:inline-flex">
            <Glyph name="plus" size={14} /> <span className="whitespace-nowrap">Admit</span>
          </button>
          <button type="button" className="relative ae-btn ae-btn-ghost px-2.5" aria-label="Notifications">
            <Glyph name="bell" size={15} />
            <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-[#E8788E] ring-2 ring-white" />
          </button>
          <button type="button" className="ae-btn ae-btn-primary">
            <Glyph name="sparkle" size={14} /> <span className="whitespace-nowrap">Co-pilot</span>
          </button>
        </div>
      </div>
    </header>
  );
}
`,

  'src/components/PatientFocus.js': `import React from 'react';
import Glyph from './Glyph';
import { FOCUS_PATIENT, VITALS, ECG_POINTS } from '../data/clinical';

function ecgPath(points, w, h) {
  const max = Math.max(...points);
  const min = Math.min(...points);
  const span = max - min || 1;
  return points
    .map((p, i) => {
      const x = (i / (points.length - 1)) * w;
      const y = h - ((p - min) / span) * h * 0.85 - h * 0.075;
      return (i === 0 ? 'M' : 'L') + x.toFixed(1) + ' ' + y.toFixed(1);
    })
    .join(' ');
}

function VitalCell({ v }) {
  const pct = v.text ? 0.5 : Math.min(1, Math.max(0, (v.value - v.min) / (v.max - v.min)));
  const radius = 22;
  const circ = 2 * Math.PI * radius;
  return (
    <div className="flex min-w-0 items-center gap-3 rounded-xl border border-[#ECE6FB] bg-white p-3">
      {v.text ? (
        <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#F2EBFC] to-[#E7DDFA] sm:h-14 sm:w-14">
          <Glyph name={v.icon} size={18} className="text-[#6C5CE7]" />
        </div>
      ) : (
        <div className="relative h-12 w-12 shrink-0 sm:h-14 sm:w-14">
          <svg width="100%" height="100%" viewBox="0 0 56 56" className="-rotate-90">
            <circle cx="28" cy="28" r={radius} fill="none" stroke="#F0E9FB" strokeWidth="4" />
            <circle
              cx="28" cy="28" r={radius}
              fill="none"
              stroke={v.color}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={circ}
              strokeDashoffset={circ * (1 - pct)}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <Glyph name={v.icon} size={15} className="text-[#615F7E]" />
          </div>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="ae-truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8B85AB]">{v.label}</p>
        <p className="font-mono ae-tnum mt-1 flex items-baseline gap-1 text-[16px] font-semibold leading-none text-[#1F1B3A] sm:text-[18px]">
          <span className="ae-truncate">{v.value}</span>
          <span className="shrink-0 text-[10px] font-medium text-[#8B85AB]">{v.unit}</span>
        </p>
        <p className="mt-1.5 text-[10px] text-[#5EC5C7]">Normal range</p>
      </div>
    </div>
  );
}

export default function PatientFocus() {
  const w = 620;
  const h = 110;
  const path = ecgPath(ECG_POINTS, w, h);

  return (
    <section className="ae-panel relative overflow-hidden p-4 sm:p-5 md:p-6">
      <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-[#F2EBFC] opacity-70 blur-2xl" />
      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3 sm:gap-4">
          <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#6C5CE7] to-[#8B7BF0] text-white shadow-[0_14px_30px_-12px_rgba(108,92,231,0.55)] sm:h-14 sm:w-14">
            <span className="font-display text-base font-bold sm:text-lg">OB</span>
            <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-white">
              <span className="ae-status-dot" />
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display ae-truncate max-w-full text-[18px] font-bold text-[#1F1B3A] sm:text-xl">{FOCUS_PATIENT.name}</h2>
              <span className="ae-tag ae-tag-violet shrink-0">In focus</span>
            </div>
            <p className="ae-clamp-1 mt-1 text-[11.5px] text-[#615F7E]">
              {FOCUS_PATIENT.id} · {FOCUS_PATIENT.age} yrs · {FOCUS_PATIENT.sex} · {FOCUS_PATIENT.room}
            </p>
            <p className="ae-clamp-2 mt-2 text-[12.5px] font-semibold text-[#1F1B3A]">
              {FOCUS_PATIENT.primary} <span className="ml-1 text-[11px] font-normal text-[#615F7E]">· attending {FOCUS_PATIENT.attending}</span>
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <span className="ae-tag ae-tag-mint whitespace-nowrap">Risk · {FOCUS_PATIENT.riskScore}</span>
          <button type="button" className="ae-btn ae-btn-ghost whitespace-nowrap">Chart <Glyph name="arrow" size={13} /></button>
        </div>
      </div>

      <div className="relative mt-5 rounded-2xl border border-[#ECE6FB] bg-gradient-to-br from-[#FBF9FF] to-[#F4EFFD] p-3.5 sm:mt-6 sm:p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="ae-truncate text-[10px] font-bold uppercase tracking-[0.16em] text-[#6C5CE7]">Live ECG · lead II</p>
            <p className="font-mono ae-tnum mt-1 flex flex-wrap items-baseline gap-x-1.5 text-[20px] font-semibold text-[#1F1B3A] sm:text-[22px]">
              <span>78</span>
              <span className="text-[11.5px] font-medium text-[#615F7E] sm:text-[12px]">bpm · sinus rhythm</span>
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="ae-tag ae-tag-mint whitespace-nowrap"><span className="h-1.5 w-1.5 rounded-full bg-[#5EC5C7]" /> Streaming</span>
            <span className="font-mono ae-tnum text-[10px] text-[#8B85AB]">12:38:04</span>
          </div>
        </div>
        <svg viewBox={'0 0 ' + w + ' ' + h} className="ae-grid-bg mt-3 w-full" preserveAspectRatio="none" style={{ height: h, maxHeight: 110 }}>
          <defs>
            <linearGradient id="ecg-grad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#6C5CE7" />
              <stop offset="60%" stopColor="#8B7BF0" />
              <stop offset="100%" stopColor="#5EC5C7" />
            </linearGradient>
          </defs>
          <path d={path} fill="none" stroke="url(#ecg-grad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:mt-5 sm:grid-cols-2 xl:grid-cols-4">
        {VITALS.map((v) => (
          <VitalCell key={v.label} v={v} />
        ))}
      </div>
    </section>
  );
}
`,

  'src/components/ClinicalInsights.js': `import React from 'react';
import Glyph from './Glyph';
import { INSIGHTS, COHORT_BARS } from '../data/clinical';

const TONE_RING = { mint: '#5EC5C7', violet: '#6C5CE7', amber: '#E6A756', rose: '#E8788E' };
const TONE_TAG = { mint: 'ae-tag-mint', violet: 'ae-tag-violet', amber: 'ae-tag-amber', rose: 'ae-tag-rose' };

function Ring({ pct, color }) {
  const r = 18;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative h-12 w-12 shrink-0">
      <svg width="48" height="48" viewBox="0 0 48 48" className="-rotate-90">
        <circle cx="24" cy="24" r={r} fill="none" stroke="#F0E9FB" strokeWidth="3.5" />
        <circle cx="24" cy="24" r={r} fill="none" stroke={color} strokeWidth="3.5" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} />
      </svg>
      <p className="font-mono absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-[#1F1B3A]">{pct}</p>
    </div>
  );
}

export default function ClinicalInsights() {
  return (
    <div className="flex min-w-0 flex-col gap-4 lg:gap-5">
      <section className="ae-panel-ink relative overflow-hidden p-4 sm:p-5">
        <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br from-[#6C5CE7]/40 to-[#5EC5C7]/30 blur-2xl" />
        <div className="relative flex items-center gap-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/10">
            <Glyph name="sparkle" size={14} />
          </div>
          <p className="ae-truncate text-[10px] font-bold uppercase tracking-[0.18em] text-[#B7ACEF]">AI Clinical Co-pilot</p>
        </div>
        <p className="font-display ae-clamp-2 mt-3 text-[16px] font-bold leading-snug text-white sm:text-[18px]">3 new insights for your patients today.</p>
        <p className="ae-clamp-2 mt-2 text-[12px] leading-relaxed text-[#C9C0F2]">
          Trained on 1.4M clinical records. Reviewed and HIPAA aligned.
        </p>
        <button type="button" className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[11px] font-semibold text-[#5949C9]">
          <span className="whitespace-nowrap">Open co-pilot</span> <Glyph name="chev" size={13} />
        </button>
      </section>

      <section className="ae-panel p-4 sm:p-5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-display ae-truncate text-[14px] font-bold text-[#1F1B3A]">Predictive insights</h3>
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8B85AB]">Live</span>
        </div>
        <div className="mt-4 space-y-3">
          {INSIGHTS.map((i) => (
            <div key={i.title} className="rounded-xl border border-[#ECE6FB] bg-[#FBF9FF] p-3 transition hover:border-[#DCD3F7] hover:bg-white">
              <div className="flex items-start gap-3">
                <Ring pct={i.score} color={TONE_RING[i.tone]} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="ae-clamp-2 text-[12.5px] font-semibold leading-snug text-[#1F1B3A]">{i.title}</p>
                    <span className={'ae-tag shrink-0 whitespace-nowrap ' + TONE_TAG[i.tone]}>{i.chip}</span>
                  </div>
                  <p className="ae-clamp-2 mt-1 text-[11px] leading-relaxed text-[#615F7E]">{i.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="ae-panel-tinted p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="font-display ae-truncate text-[14px] font-bold text-[#1F1B3A]">Cohort distribution</h3>
            <p className="ae-clamp-1 mt-1 text-[10.5px] text-[#615F7E]">123 active cases · 5 departments</p>
          </div>
          <Glyph name="shield" size={16} className="shrink-0 text-[#6C5CE7]" />
        </div>
        <div className="mt-5 space-y-3">
          {COHORT_BARS.map((b) => (
            <div key={b.label} className="min-w-0">
              <div className="flex items-center justify-between gap-2 text-[11px]">
                <span className="ae-truncate font-semibold text-[#1F1B3A]">{b.label}</span>
                <span className="font-mono ae-tnum shrink-0 text-[#615F7E]">{b.value}</span>
              </div>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/70">
                <div style={{ width: Math.min(100, b.value * 2) + '%' }} className="h-full rounded-full bg-gradient-to-r from-[#6C5CE7] via-[#8B7BF0] to-[#5EC5C7]" />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
`,

  'src/components/CareQueue.js': `import React from 'react';
import Glyph from './Glyph';
import { CARE_QUEUE } from '../data/clinical';

const TONE_ACCENT = {
  mint: { ring: '#5EC5C7', tag: 'ae-tag-mint' },
  violet: { ring: '#6C5CE7', tag: 'ae-tag-violet' },
  amber: { ring: '#E6A756', tag: 'ae-tag-amber' },
  rose: { ring: '#E8788E', tag: 'ae-tag-rose' },
  slate: { ring: '#615F7E', tag: 'ae-tag-slate' },
};

export default function CareQueue() {
  return (
    <section className="ae-panel relative h-full overflow-hidden p-4 sm:p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="font-display ae-truncate text-[14px] font-bold text-[#1F1B3A]">Care queue</h3>
          <p className="ae-clamp-1 mt-1 text-[10.5px] text-[#615F7E]">Today · 5 sessions · 2 awaiting</p>
        </div>
        <span className="ae-tag ae-tag-mint shrink-0 whitespace-nowrap"><span className="h-1.5 w-1.5 rounded-full bg-[#5EC5C7]" /> Live</span>
      </div>

      <div className="mt-5 space-y-3">
        {CARE_QUEUE.map((q, i) => {
          const tone = TONE_ACCENT[q.tone];
          return (
            <div key={q.time + q.name} className="group relative rounded-xl border border-[#ECE6FB] bg-white p-3 transition hover:border-[#DCD3F7] hover:shadow-[0_8px_18px_-12px_rgba(108,92,231,0.35)]">
              <div className="flex items-start gap-3">
                <div className="flex shrink-0 flex-col items-center">
                  <span className="font-mono ae-tnum text-[11px] font-semibold text-[#1F1B3A]">{q.time}</span>
                  <span style={{ background: tone.ring }} className="mt-2 h-2.5 w-2.5 rounded-full ring-4 ring-[#F6F3FD]" />
                  {i < CARE_QUEUE.length - 1 && (
                    <span className="mt-1 h-8 w-px bg-gradient-to-b from-[#DCD3F7] to-transparent" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="ae-truncate text-[12.5px] font-semibold text-[#1F1B3A]">{q.name}</p>
                    <span className={'ae-tag shrink-0 whitespace-nowrap ' + tone.tag}>{q.status}</span>
                  </div>
                  <p className="ae-clamp-2 mt-1 text-[11px] leading-relaxed text-[#615F7E]">{q.desc}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button type="button" className="ae-btn ae-btn-ghost mt-5 w-full justify-center">
        <span className="whitespace-nowrap">Open full schedule</span> <Glyph name="arrow" size={13} />
      </button>
    </section>
  );
}
`,

  'src/components/DepartmentMetrics.js': `import React from 'react';
import Glyph from './Glyph';
import { DEPT_LINE, TREATMENT_MIX, DEPT_KPI } from '../data/clinical';

function buildArea(data, w, h, key, padY = 14) {
  const max = Math.max(...data.map((d) => d[key]));
  const min = 0;
  const step = w / (data.length - 1);
  const points = data.map((d, i) => {
    const x = i * step;
    const y = h - padY - ((d[key] - min) / (max - min || 1)) * (h - padY * 2);
    return [x, y];
  });
  const line = points.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const area = line + ' L ' + w + ' ' + h + ' L 0 ' + h + ' Z';
  return { line, area, points };
}

function Donut() {
  const r = 52;
  const cx = 64;
  const cy = 64;
  const circ = 2 * Math.PI * r;
  const total = TREATMENT_MIX.reduce((a, b) => a + b.value, 0);
  let acc = 0;
  return (
    <div className="relative">
      <svg width="128" height="128" viewBox="0 0 128 128" className="-rotate-90">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F0E9FB" strokeWidth="12" />
        {TREATMENT_MIX.map((t, i) => {
          const len = (t.value / total) * circ;
          const offset = circ - acc;
          acc += len;
          return (
            <circle
              key={t.name}
              cx={cx} cy={cy} r={r}
              fill="none"
              stroke={t.color}
              strokeWidth="12"
              strokeDasharray={len + ' ' + (circ - len)}
              strokeDashoffset={offset}
              strokeLinecap="butt"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="font-mono text-[20px] font-semibold text-[#1F1B3A]">{total}</p>
        <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8B85AB]">Plans</p>
      </div>
    </div>
  );
}

export default function DepartmentMetrics() {
  const w = 600;
  const h = 160;
  const visits = buildArea(DEPT_LINE, w, h, 'visits');
  const admits = buildArea(DEPT_LINE, w, h, 'admits');

  return (
    <section className="ae-panel min-w-0 overflow-hidden p-4 sm:p-5 md:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="ae-truncate text-[10px] font-bold uppercase tracking-[0.18em] text-[#6C5CE7]">Department intelligence</p>
          <h3 className="font-display ae-clamp-1 mt-1.5 text-[16px] font-bold text-[#1F1B3A] sm:text-[18px]">Encounters &amp; admissions · week</h3>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-3 text-[11px] sm:gap-4">
          <span className="flex items-center gap-1.5 whitespace-nowrap text-[#615F7E]">
            <span className="inline-block h-1.5 w-3 rounded-full bg-[#6C5CE7]" /> Visits
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap text-[#615F7E]">
            <span className="inline-block h-1.5 w-3 rounded-full bg-[#5EC5C7]" /> Admissions
          </span>
        </div>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-[1.6fr_1fr] lg:gap-5">
        <div className="min-w-0">
          <svg viewBox={'0 0 ' + w + ' ' + h} className="w-full" preserveAspectRatio="none" style={{ height: h, maxHeight: 200 }}>
            <defs>
              <linearGradient id="dept-violet" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6C5CE7" stopOpacity="0.32" />
                <stop offset="100%" stopColor="#6C5CE7" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="dept-mint" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#5EC5C7" stopOpacity="0.32" />
                <stop offset="100%" stopColor="#5EC5C7" stopOpacity="0" />
              </linearGradient>
            </defs>
            <g stroke="#F0E9FB" strokeDasharray="3 4">
              {[0.25, 0.5, 0.75].map((t) => (
                <line key={t} x1="0" x2={w} y1={h * t} y2={h * t} />
              ))}
            </g>
            <path d={visits.area} fill="url(#dept-violet)" />
            <path d={admits.area} fill="url(#dept-mint)" />
            <path d={visits.line} fill="none" stroke="#6C5CE7" strokeWidth="2.4" strokeLinecap="round" />
            <path d={admits.line} fill="none" stroke="#5EC5C7" strokeWidth="2.4" strokeLinecap="round" />
            {visits.points.map((p, i) => (
              <circle key={'v' + i} cx={p[0]} cy={p[1]} r="3.5" fill="#fff" stroke="#6C5CE7" strokeWidth="2" />
            ))}
          </svg>
          <div className="mt-3 flex justify-between text-[10px] font-semibold text-[#8B85AB]">
            {DEPT_LINE.map((d) => (
              <span key={d.d}>{d.d}</span>
            ))}
          </div>
        </div>

        <div className="min-w-0 rounded-2xl border border-[#ECE6FB] bg-gradient-to-br from-[#FBF9FF] to-[#F4EFFD] p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="ae-truncate text-[10px] font-bold uppercase tracking-[0.14em] text-[#6C5CE7]">Care plans</p>
              <p className="font-display ae-truncate mt-1 text-[14px] font-bold text-[#1F1B3A]">Treatment mix</p>
            </div>
            <Glyph name="sparkle" size={14} className="shrink-0 text-[#6C5CE7]" />
          </div>
          <div className="mt-3 flex items-center justify-center">
            <Donut />
          </div>
          <ul className="mt-3 space-y-1.5">
            {TREATMENT_MIX.map((t) => (
              <li key={t.name} className="flex items-center justify-between gap-2 text-[11px]">
                <span className="flex min-w-0 items-center gap-2 text-[#1F1B3A]">
                  <span style={{ background: t.color }} className="h-2 w-2 shrink-0 rounded-full" />
                  <span className="ae-truncate">{t.name}</span>
                </span>
                <span className="font-mono ae-tnum shrink-0 text-[#615F7E]">{t.value}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="ae-divider mt-6" />

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
        {DEPT_KPI.map((k) => (
          <div key={k.label} className="min-w-0 rounded-xl border border-[#ECE6FB] bg-white p-3 sm:p-3.5">
            <p className="ae-truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8B85AB]">{k.label}</p>
            <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <p className="font-mono ae-tnum text-[18px] font-semibold text-[#1F1B3A] sm:text-[20px]">{k.value}</p>
              <span className={'whitespace-nowrap text-[10.5px] font-semibold ' + (k.good ? 'text-[#2E8B8D]' : 'text-[#B14463]')}>
                {k.delta}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
`,

  'src/components/AlertFeed.js': `import React from 'react';
import Glyph from './Glyph';
import { ALERTS } from '../data/clinical';

const TONE_TAG = { mint: 'ae-tag-mint', violet: 'ae-tag-violet', amber: 'ae-tag-amber', rose: 'ae-tag-rose', slate: 'ae-tag-slate' };
const TONE_BG = {
  rose: 'from-[#FFF1F4] to-[#FFE7EE]',
  amber: 'from-[#FFF6E9] to-[#FFEFD7]',
  violet: 'from-[#F4EFFD] to-[#ECE2FB]',
  mint: 'from-[#E8F8F6] to-[#D7F1EE]',
  slate: 'from-[#F0EFF4] to-[#E7E6EE]',
};
const TONE_ICON = {
  rose: { bg: 'bg-[#E8788E]', icon: 'heart' },
  amber: { bg: 'bg-[#E6A756]', icon: 'bell' },
  violet: { bg: 'bg-[#6C5CE7]', icon: 'msg' },
  mint: { bg: 'bg-[#5EC5C7]', icon: 'shield' },
};

export default function AlertFeed() {
  return (
    <section className="ae-panel h-full overflow-hidden p-4 sm:p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="font-display ae-truncate text-[14px] font-bold text-[#1F1B3A]">Activity feed</h3>
          <p className="ae-clamp-1 mt-1 text-[10.5px] text-[#615F7E]">Clinical alerts, notes &amp; system events</p>
        </div>
        <span className="ae-tag ae-tag-violet shrink-0 whitespace-nowrap">4 new</span>
      </div>

      <ol className="relative mt-5 space-y-3">
        {ALERTS.map((a) => {
          const ic = TONE_ICON[a.tone] || TONE_ICON.violet;
          return (
            <li key={a.title} className={'group relative overflow-hidden rounded-xl border border-white bg-gradient-to-br ' + TONE_BG[a.tone] + ' p-3 transition hover:shadow-[0_10px_24px_-14px_rgba(31,27,58,0.18)]'}>
              <div className="flex items-start gap-3">
                <div className={'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white ' + ic.bg}>
                  <Glyph name={ic.icon} size={15} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="ae-clamp-2 text-[12.5px] font-semibold leading-snug text-[#1F1B3A]">{a.title}</p>
                    <span className="font-mono ae-tnum shrink-0 whitespace-nowrap text-[10px] text-[#615F7E]">{a.time}</span>
                  </div>
                  <p className="ae-clamp-2 mt-0.5 text-[11px] leading-relaxed text-[#4A4768]">{a.desc}</p>
                  <span className={'ae-tag mt-2 whitespace-nowrap ' + TONE_TAG[a.tone]}>{a.kind}</span>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
`,

  'src/components/Footer.js': `import React from 'react';
import Glyph from './Glyph';

export default function Footer() {
  return (
    <footer className="mt-8 flex flex-col items-start justify-between gap-4 border-t border-[#ECE6FB] pt-5 text-[11px] text-[#615F7E] md:flex-row md:items-center md:gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#6C5CE7] to-[#8B7BF0] text-white">
          <Glyph name="pulse" size={13} strokeWidth={2.2} />
        </div>
        <p className="ae-clamp-2 leading-snug">© 2026 Aether Health · HIPAA · SOC 2 Type II · HL7 FHIR</p>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-2 font-semibold">
        <a href="#privacy" className="whitespace-nowrap hover:text-[#6C5CE7]">Privacy</a>
        <a href="#terms" className="whitespace-nowrap hover:text-[#6C5CE7]">Terms</a>
        <a href="#docs" className="whitespace-nowrap hover:text-[#6C5CE7]">Clinician docs</a>
        <a href="#status" className="flex items-center gap-1.5 whitespace-nowrap hover:text-[#6C5CE7]">
          <span className="ae-status-dot" /> All systems normal
        </a>
      </div>
    </footer>
  );
}
`,
};
